import React, { ReactNode, useMemo } from 'react'
import {
  Box,
  Text,
  Button,
  Image,
  Heading,
  TableContainer,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  SimpleGrid,
  GridItem,
} from '@chakra-ui/react'
import { CloseIcon, ChevronLeftIcon } from '@chakra-ui/icons'
import { useActiveWeb3React } from 'app/services/web3'
import { getAddress } from '@ethersproject/address'
import { Token } from 'sdk'
import { CurrencyLogo } from 'app/components/CurrencyLogo'
import ChartCard from 'app/features/analytics/ChartCard'
import { LegacyTransactions } from 'app/features/transactions/Transactions'
import { getExplorerLink } from 'app/functions/explorer'
import { formatNumber, formatPercent, shortenAddress } from 'app/functions/format'
import { useTotalSupply } from 'app/hooks/useTotalSupply'
import {
  useNativePrice,
  useOneDayBlock,
  useTokenDayData,
  useTokenPairs,
  useTokens,
  useTwoDayBlock,
  useOneWeekBlock,
} from 'app/services/graph'
import PoolList from 'app/features/pool/analytics/PoolList'
import useCopyClipboard from 'app/hooks/useCopyClipboard'

const chartTimespans = [
  {
    text: '1W',
    length: 604800,
  },
  {
    text: '1M',
    length: 2629746,
  },
  {
    text: '1Y',
    length: 31556952,
  },
  {
    text: 'ALL',
    length: Infinity,
  },
]

interface TokenDetailProps {
  selectedToken: any
  isOpen: boolean
  onClose(): void
  onOpenPool(x: ReactNode): void
  isCloseButton: boolean
}

export default function TokenDetail({
  selectedToken,
  isOpen,
  onClose,
  onOpenPool,
  isCloseButton = false,
}: TokenDetailProps) {
  const id = selectedToken ? selectedToken.token.id : ''
  const { chainId } = useActiveWeb3React()

  const block1d = useOneDayBlock({ chainId })
  const block2d = useTwoDayBlock({ chainId })
  const block1w = useOneWeekBlock({ chainId })

  const [isCopied, setCopied] = useCopyClipboard()

  // General data (volume, liquidity)
  const nativePrice = useNativePrice({ chainId })
  const nativePrice1d = useNativePrice({ chainId, variables: { block: block1d }, shouldFetch: !!block1d })

  const token = useTokens({ chainId, variables: { where: { id } }, shouldFetch: !!id })?.[0]
  const token1d = useTokens({
    chainId,
    variables: { block: block1d, where: { id } },
    shouldFetch: !!id && !!block1d,
  })?.[0]
  const token2d = useTokens({
    chainId,
    variables: { block: block2d, where: { id } },
    shouldFetch: !!id && !!block2d,
  })?.[0]

  // Token Pairs
  const tokenPairs = useTokenPairs({ chainId, variables: { id } })
  const tokenPairs1d = useTokenPairs({
    chainId,
    variables: { id, block: block1d },
    shouldFetch: !!id && !!block1d,
  })
  const tokenPairs1w = useTokenPairs({
    chainId,
    variables: { id, block: block1w },
    shouldFetch: !!id && !!block1w,
  })

  const tokenPairsFormatted = useMemo(
    () =>
      /* @ts-ignore TYPE NEEDS FIXING */
      tokenPairs?.map((pair) => {
        /* @ts-ignore TYPE NEEDS FIXING */
        const pair1d = tokenPairs1d?.find((p) => pair.id === p.id) ?? pair
        /* @ts-ignore TYPE NEEDS FIXING */
        const pair1w = tokenPairs1w?.find((p) => pair.id === p.id) ?? pair1d

        return {
          pair: {
            token0: pair.token0,
            token1: pair.token1,
            id: pair.id,
          },
          liquidity: pair.reserveUSD,
          volume1d: pair.volumeUSD - pair1d.volumeUSD,
          volume1w: pair.volumeUSD - pair1w.volumeUSD,
        }
      }),
    [tokenPairs, tokenPairs1d, tokenPairs1w]
  )

  // For the Info Cards
  const price = token?.derivedETH * nativePrice
  const priceChange = ((token?.derivedETH * nativePrice) / (token1d?.derivedETH * nativePrice1d)) * 100 - 100

  const liquidityUSD = token?.liquidity * token?.derivedETH * nativePrice
  const liquidityUSDChange =
    ((token?.liquidity * price) / (token1d?.liquidity * token1d?.derivedETH * nativePrice1d)) * 100 - 100

  const volumeUSD1d = token?.volumeUSD - token1d?.volumeUSD
  const volumeUSD2d = token1d?.volumeUSD - token2d?.volumeUSD
  const volumeUSD1dChange = (volumeUSD1d / volumeUSD2d) * 100 - 100

  // The Chart
  const tokenDayData = useTokenDayData({
    chainId,
    variables: { where: { token: id.toLowerCase() } },
    shouldFetch: !!id && !!chainId,
  })

  const chartData = useMemo(
    () => ({
      liquidityChart: tokenDayData
        /* @ts-ignore TYPE NEEDS FIXING */
        ?.sort((a, b) => a.date - b.date)
        /* @ts-ignore TYPE NEEDS FIXING */
        .map((day) => ({ x: new Date(day.date * 1000), y: Number(day.liquidityUSD) })),

      volumeChart: tokenDayData
        /* @ts-ignore TYPE NEEDS FIXING */
        ?.sort((a, b) => a.date - b.date)
        /* @ts-ignore TYPE NEEDS FIXING */
        .map((day) => ({ x: new Date(day.date * 1000), y: Number(day.volumeUSD) })),
    }),
    [tokenDayData]
  )

  const currency = token
    ? new Token(chainId, getAddress(id), Number(token?.decimals) || 18, token?.symbol, token?.name)
    : undefined

  const totalSupply = useTotalSupply(currency)

  if (!token) return <></>
  console.log('detail-token', token)

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} autoFocus={false}>
        <ModalOverlay className="full_overlay" />
        <ModalContent className="full_modal_as full_modal_as02 full_modal_as_pools">
          <Box className="avx_eth_raw_prnt avx_eth_raw_prnt_nls">
            <Box className="avx_eth_raw nls_new_eth_bx">
              <Box className="nls_oxb_new_main">
                <Box className="nls_olx_tokn_main">
                  <CurrencyLogo className="avx_bg_ic" currency={currency} size={60} />
                  <Heading as="h1" className="avx_eth_h1">
                    {token?.name}
                  </Heading>
                </Box>
                <Box className="nls_oxb_bx">
                  <Heading as="h4">{shortenAddress(id)}</Heading>
                  <Button className='copy_as_btn box_shadov_none_btn' onClick={() => setCopied(id)}>
                    <Image src="/img/obx_icon_nls.svg" alt="" className="obx_icon_nls"/>
                  </Button>
                </Box>
              </Box>

              <Text className="avx_eth_p avx_eth_p_nls">Dive deeper in the analytics of the {token?.name}.</Text>
            </Box>
            <Box className="prise_market_nls">
              <Box className="price_mrkt_mx_nls">
                <Text>Price</Text>
                <Heading as="h3">
                  {formatNumber(price ?? 0, true)}
                </Heading>
              </Box>
              <Box className="">
                <Text>Market Cap</Text>
                <Heading as="h3">
                  {formatNumber(price * Number(totalSupply?.toExact()) ?? 0, true, false)}
                </Heading>
              </Box>
            </Box>
          </Box>

          <ModalCloseButton onClick={onClose} className="box_shadov_none_btn stkng_back_btn click_btn_as">
            <ChevronLeftIcon /> Back to Staking
          </ModalCloseButton>
          {isCloseButton ? (
            <ModalCloseButton className="box_shadov_none_btn">
              Close <CloseIcon />
            </ModalCloseButton>
          ) : null}
          <Box className="full_modal_as_inn">
            <Box className="anltcs_ovrviv_box">
              <Heading as="h4" className="anltcs_ovrviv_h4">
                Analytics Overview
              </Heading>
              <SimpleGrid columns={12} columnGap={3} rowGap={3}>
                <GridItem colSpan={[12, 12, 12, 6]}>
                  <Box className="grf_img_prnt">
                    <ChartCard
                      header="Liquidity"
                      subheader={token?.symbol}
                      figure={liquidityUSD}
                      change={liquidityUSDChange}
                      chart={chartData.liquidityChart}
                      defaultTimespan="1W"
                      timespans={chartTimespans}
                    />
                  </Box>
                </GridItem>
                <GridItem colSpan={[12, 12, 12, 6]}>
                  <Box className="grf_img_prnt">
                    <ChartCard
                      header="Volume"
                      subheader={token?.symbol}
                      figure={volumeUSD1d}
                      change={volumeUSD1dChange}
                      chart={chartData.volumeChart}
                      defaultTimespan="1W"
                      timespans={chartTimespans}
                    />
                  </Box>
                </GridItem>
              </SimpleGrid>
            </Box>
            <Box className="avx_eth_info_box">
              <SimpleGrid columns={12} columnGap={3} rowGap={3}>
                <GridItem colSpan={[12, 12, 6, 4]}>
                  <Box className="lvftau_bx">
                    <Text className="lvftau_p">Liquidity (24h)</Text>
                    <Heading as="h4">
                      {formatNumber(liquidityUSD, true, false)}{' '}
                      <span className={liquidityUSDChange >= 0 ? 'lite_green_spn' : 'red_spn'}>
                        {formatPercent(liquidityUSDChange)}
                      </span>
                    </Heading>
                  </Box>
                </GridItem>
                <GridItem colSpan={[12, 12, 6, 4]}>
                  <Box className="lvftau_bx">
                    <Text className="lvftau_p">Volume (24h)</Text>
                    <Heading as="h4">
                      {formatNumber(volumeUSD1d, true, false)}{' '}
                      <span className={volumeUSD1dChange >= 0 ? 'lite_green_spn' : 'red_spn'}>
                        {formatPercent(volumeUSD1dChange)}
                      </span>
                    </Heading>
                  </Box>
                </GridItem>
                <GridItem colSpan={[12, 12, 6, 4]}>
                  <Box className="lvftau_bx">
                    <Text className="lvftau_p">Fees (24h)</Text>
                    <Heading as="h4">
                      {volumeUSD1d * 0.003}{' '}
                      <span className={volumeUSD1dChange >= 0 ? 'lite_green_spn' : 'red_spn'}>
                        {formatPercent(volumeUSD1dChange)}
                      </span>
                    </Heading>
                  </Box>
                </GridItem>
              </SimpleGrid>
            </Box>
            <Box className="avx_eth_info_box">
              <Box className="infrmtn_mim_bx">
                <Heading as="h3" className="inf0_mim_h">
                  Information
                </Heading>
                <SimpleGrid columns={12} columnGap={3} rowGap={3}>
                  <GridItem colSpan={[12, 12, 6, 4]}>
                    <Box className="mim_addrs_bx">
                      <Text className="lite_p">Name</Text>
                      <Heading as="h5" className="copy_text_btn">
                        <span>{token?.name}</span>
                      </Heading>
                    </Box>
                  </GridItem>
                  <GridItem colSpan={[12, 12, 6, 4]}>
                    <Box className="mim_addrs_bx">
                      <Text className="lite_p">Symbol</Text>
                      <Heading as="h5" className="copy_text_btn">
                        <span>{token?.symbol}</span>
                      </Heading>
                    </Box>
                  </GridItem>
                  <GridItem colSpan={[12, 12, 6, 4]}>
                    <Box className="mim_addrs_bx">
                      <Text className="lite_p">Address</Text>
                      <Heading as="h5" className="copy_text_btn">
                        <span>{id}</span>{' '}
                        <Button
                          as="a"
                          href={getExplorerLink(chainId, id, 'token')}
                          target="_blank"
                          className="copy_as_btn box_shadov_none_btn"
                        >
                          <Image src="/img/copy_as_ic.svg" />
                        </Button>
                      </Heading>
                    </Box>
                  </GridItem>
                </SimpleGrid>
              </Box>
            </Box>
            <Box className="anltcs_ovrviv_box anltcs_ovrviv_box02">
              <Box className="pool_teb_prnt">
                <Box className="swap_main staking_main">
                  <Box className="text_img text_imgv2">
                    <Heading as="h6">Top Pools</Heading>
                    {/* <Box className='search_btn_stak'>
                      <Image src='./img/search_icon.svg' alt='' className='serch_icon_stakk' />
                      <Input type="text" placeholder="Search Token Pair" />
                    </Box> */}
                  </Box>
                  <Box className="def_table">
                    <TableContainer>
                      <PoolList pairsData={tokenPairsFormatted} />
                      {/* <Box className='teb_bot_tex'>
                        <ArrowBackIcon />
                        <Text> <span>Page</span> 1 of 20</Text>
                        <ArrowForwardIcon />
                      </Box> */}
                    </TableContainer>
                  </Box>
                </Box>
              </Box>
            </Box>
            <LegacyTransactions pairs={tokenPairs ? tokenPairs.map((pair: any) => pair.id) : []} />
            {/* 
            <Box className='anltcs_ovrviv_box'>
              <Box className='pool_teb_prnt'>
                <Box className="swap_main staking_main">
                  <Box className='text_img text_imgv2'>
                    <Heading as="h6">Top Tokens</Heading>
                    <Box className='search_btn_stak'>
                      <Image src='./img/search_icon.svg' alt='' className='serch_icon_stakk' />
                      <Input type="text" placeholder="Search Token Pair" />
                    </Box>
                  </Box>
                  <Box className='def_table'>
                    <TableContainer>
                      <Table variant='simple'>
                        <Thead className='first_line_tbl'>
                          <Tr className=''>
                            <Th align='left'><b>#</b></Th>
                            <Th>Token</Th>
                            <Th>Liquidity</Th>
                            <Th>Volume (24H) </Th>
                            <Th>Fees (24H)</Th>
                            <Th>APR</Th>
                          </Tr>
                        </Thead>
                        <Tbody className='last_three_tbl'>
                          <Tr className=''>
                            <Td>
                              <Box className='for_line_tble for_line_tble_va'>
                                <Image src='./img/hdr_btn_icon.svg' alt='' className='hdr_btn_had hdr_btn_hadv2 ' />
                                <Image src='./img/ethre_ava_ic.svg' alt='' className='hdr_btn_had hdr_btn_hadv2 ethre_ava_ic' />
                              </Box>
                            </Td>
                            <Td>AVAX/ETH</Td>
                            <Td>$11 507 917,63</Td>
                            <Td>$17 195,37</Td>
                            <Td>$51.59</Td>
                            <Td>0.14%</Td>
                          </Tr>
                          <Tr className=''>
                            <Td>
                              <Box className='for_line_tble for_line_tble_va'>
                                <Image src='./img/hdr_btn_icon.svg' alt='' className='hdr_btn_had hdr_btn_hadv2' />
                                <Image src='./img/ethre_ava_ic.svg' alt='' className='hdr_btn_had hdr_btn_hadv2 ethre_ava_ic' />
                              </Box>
                            </Td>
                            <Td>AVAX/ETH</Td>
                            <Td>$11 507 917,63</Td>
                            <Td>$17 195,37</Td>
                            <Td>$51.59</Td>
                            <Td>0.14%</Td>
                          </Tr>
                          <Tr className=''>
                            <Td>
                              <Box className='for_line_tble for_line_tble_va'>
                                <Image src='./img/hdr_btn_icon.svg' alt='' className='hdr_btn_had hdr_btn_hadv2' />
                                <Image src='./img/ethre_ava_ic.svg' alt='' className='hdr_btn_had hdr_btn_hadv2 ethre_ava_ic' />
                              </Box>
                            </Td>
                            <Td>AVAX/ETH</Td>
                            <Td>$11 507 917,63</Td>
                            <Td>$17 195,37</Td>
                            <Td>$51.59</Td>
                            <Td>0.14%</Td>
                          </Tr>
                          <Tr className=''>
                            <Td>
                              <Box className='for_line_tble for_line_tble_va'>
                                <Image src='./img/hdr_btn_icon.svg' alt='' className='hdr_btn_had hdr_btn_hadv2' />
                                <Image src='./img/ethre_ava_ic.svg' alt='' className='hdr_btn_had hdr_btn_hadv2 ethre_ava_ic' />
                              </Box>
                            </Td>
                            <Td>AVAX/ETH</Td>
                            <Td>$11 507 917,63</Td>
                            <Td>$17 195,37</Td>
                            <Td>$51.59</Td>
                            <Td>0.14%</Td>
                          </Tr>
                          <Tr className=''>
                            <Td>
                              <Box className='for_line_tble for_line_tble_va'>
                                <Image src='./img/hdr_btn_icon.svg' alt='' className='hdr_btn_had hdr_btn_hadv2' />
                                <Image src='./img/ethre_ava_ic.svg' alt='' className='hdr_btn_had hdr_btn_hadv2 ethre_ava_ic' />
                              </Box>
                            </Td>
                            <Td>AVAX/ETH</Td>
                            <Td>$11 507 917,63</Td>
                            <Td>$17 195,37</Td>
                            <Td>$51.59</Td>
                            <Td>0.14%</Td>
                          </Tr>
                          <Tr className=''>
                            <Td>
                              <Box className='for_line_tble for_line_tble_va'>
                                <Image src='./img/hdr_btn_icon.svg' alt='' className='hdr_btn_had hdr_btn_hadv2' />
                                <Image src='./img/ethre_ava_ic.svg' alt='' className='hdr_btn_had hdr_btn_hadv2 ethre_ava_ic' />
                              </Box>
                            </Td>
                            <Td>AVAX/ETH</Td>
                            <Td>$11 507 917,63</Td>
                            <Td>$17 195,37</Td>
                            <Td>$51.59</Td>
                            <Td>0.14%</Td>
                          </Tr>
                          <Tr className=''>
                            <Td>
                              <Box className='for_line_tble for_line_tble_va'>
                                <Image src='./img/hdr_btn_icon.svg' alt='' className='hdr_btn_had hdr_btn_hadv2' />
                                <Image src='./img/ethre_ava_ic.svg' alt='' className='hdr_btn_had hdr_btn_hadv2 ethre_ava_ic' />
                              </Box>
                            </Td>
                            <Td>AVAX/ETH</Td>
                            <Td>$11 507 917,63</Td>
                            <Td>$17 195,37</Td>
                            <Td>$51.59</Td>
                            <Td>0.14%</Td>
                          </Tr>
                          <Tr className=''>
                            <Td>
                              <Box className='for_line_tble for_line_tble_va'>
                                <Image src='./img/hdr_btn_icon.svg' alt='' className='hdr_btn_had hdr_btn_hadv2' />
                                <Image src='./img/ethre_ava_ic.svg' alt='' className='hdr_btn_had hdr_btn_hadv2 ethre_ava_ic' />
                              </Box>
                            </Td>
                            <Td>AVAX/ETH</Td>
                            <Td>$11 507 917,63</Td>
                            <Td>$17 195,37</Td>
                            <Td>$51.59</Td>
                            <Td>0.14%</Td>
                          </Tr>
                          <Tr className=''>
                            <Td>
                              <Box className='for_line_tble for_line_tble_va'>
                                <Image src='./img/hdr_btn_icon.svg' alt='' className='hdr_btn_had hdr_btn_hadv2' />
                                <Image src='./img/ethre_ava_ic.svg' alt='' className='hdr_btn_had hdr_btn_hadv2 ethre_ava_ic' />
                              </Box>
                            </Td>
                            <Td>AVAX/ETH</Td>
                            <Td>$11 507 917,63</Td>
                            <Td>$17 195,37</Td>
                            <Td>$51.59</Td>
                            <Td>0.14%</Td>
                          </Tr>
                          <Tr className=''>
                            <Td>
                              <Box className='for_line_tble for_line_tble_va'>
                                <Image src='./img/hdr_btn_icon.svg' alt='' className='hdr_btn_had hdr_btn_hadv2' />
                                <Image src='./img/ethre_ava_ic.svg' alt='' className='hdr_btn_had hdr_btn_hadv2 ethre_ava_ic' />
                              </Box>
                            </Td>
                            <Td>AVAX/ETH</Td>
                            <Td>$11 507 917,63</Td>
                            <Td>$17 195,37</Td>
                            <Td>$51.59</Td>
                            <Td>0.14%</Td>
                          </Tr>
                          <Tr className=''>
                            <Td>
                              <Box className='for_line_tble for_line_tble_va'>
                                <Image src='./img/hdr_btn_icon.svg' alt='' className='hdr_btn_had hdr_btn_hadv2' />
                                <Image src='./img/ethre_ava_ic.svg' alt='' className='hdr_btn_had hdr_btn_hadv2 ethre_ava_ic' />
                              </Box>
                            </Td>
                            <Td>AVAX/ETH</Td>
                            <Td>$11 507 917,63</Td>
                            <Td>$17 195,37</Td>
                            <Td>$51.59</Td>
                            <Td>0.14%</Td>
                          </Tr>
                          <Tr className=''>
                            <Td>
                              <Box className='for_line_tble for_line_tble_va'>
                                <Image src='./img/hdr_btn_icon.svg' alt='' className='hdr_btn_had hdr_btn_hadv2' />
                                <Image src='./img/ethre_ava_ic.svg' alt='' className='hdr_btn_had hdr_btn_hadv2 ethre_ava_ic' />
                              </Box>
                            </Td>
                            <Td>AVAX/ETH</Td>
                            <Td>$11 507 917,63</Td>
                            <Td>$17 195,37</Td>
                            <Td>$51.59</Td>
                            <Td>0.14%</Td>
                          </Tr>
                          <Tr className=''>
                            <Td>
                              <Box className='for_line_tble for_line_tble_va'>
                                <Image src='./img/hdr_btn_icon.svg' alt='' className='hdr_btn_had hdr_btn_hadv2' />
                                <Image src='./img/ethre_ava_ic.svg' alt='' className='hdr_btn_had hdr_btn_hadv2 ethre_ava_ic' />
                              </Box>
                            </Td>
                            <Td>AVAX/ETH</Td>
                            <Td>$11 507 917,63</Td>
                            <Td>$17 195,37</Td>
                            <Td>$51.59</Td>
                            <Td>0.14%</Td>
                          </Tr>
                          <Tr className=''>
                            <Td>
                              <Box className='for_line_tble for_line_tble_va'>
                                <Image src='./img/hdr_btn_icon.svg' alt='' className='hdr_btn_had hdr_btn_hadv2' />
                                <Image src='./img/ethre_ava_ic.svg' alt='' className='hdr_btn_had hdr_btn_hadv2 ethre_ava_ic' />
                              </Box>
                            </Td>
                            <Td>AVAX/ETH</Td>
                            <Td>$11 507 917,63</Td>
                            <Td>$17 195,37</Td>
                            <Td>$51.59</Td>
                            <Td>0.14%</Td>
                          </Tr>
                        </Tbody>
                      </Table>
                      <Box className='teb_bot_tex'>
                        <ArrowBackIcon />
                        <Text> <span>Page</span> 1 of 20</Text>
                        <ArrowForwardIcon />
                      </Box>
                    </TableContainer>
                  </Box>
                </Box>
              </Box>
            </Box> */}
          </Box>
        </ModalContent>
      </Modal>
    </>
  )
}
